import { isString } from 'lodash'

export type AnonymousData = Record<string, unknown>

export interface MappedConfig {
  property?: string
  format?: (value: unknown) => unknown
  type?: 'date' | 'commaSeparatedList'
}

export type MappedBindings = Record<string, MappedConfig>

const Timestamps: MappedBindings = {
  created_at: { property: 'created', type: 'date' },
  updated_at: { property: 'updated', type: 'date' },
}

const NumericID: MappedBindings = {
  numeric_id: { property: 'numericID' },
}

const CastID: MappedBindings = {
  id: {
    format: (value) => (isString(value) ? parseInt(value) : value),
  },
}

export type MappedPreset = 'timestamps' | 'numericID' | 'castID'

export function Mapped<Type>(
  bindings: MappedBindings,
  presets?: MappedPreset[],
  finalise?: (entity: Type) => AnonymousData
) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  return function (constructor: Function) {
    let bound = { ...bindings }
    if (presets) {
      for (const preset of presets) {
        switch (preset) {
          case 'timestamps':
            bound = { ...bound, ...Timestamps }
            break
          case 'numericID':
            bound = { ...bound, ...NumericID }
            break
          case 'castID':
            bound = { ...bound, ...CastID }
            break
        }
      }
    }
    constructor.prototype.bindings = bound
    constructor.prototype.finalise = finalise
  }
}

interface BoundEntity {
  bindings: MappedBindings
  finalise?: (entity: unknown) => AnonymousData
}

export class Mappable {
  map<Data>(attrs: Data) {
    const bound = this as unknown as BoundEntity
    for (const attr of Object.keys(attrs as AnonymousData)) {
      const config: MappedConfig =
        bound.bindings && bound.bindings[attr] ? bound.bindings[attr] : {}
      if (!config.property) {
        config.property = attr
      }
      const value = (attrs as AnonymousData)[attr]
      if (value === undefined || value === null) {
        ;(this as AnonymousData)[config.property] = undefined
      } else if (value instanceof Array) {
        ;(this as AnonymousData)[config.property] = value.map((inner) =>
          config.format ? config.format(inner) : inner
        )
      } else {
        switch (config.type) {
          case 'date':
            ;(this as AnonymousData)[config.property] = new Date(
              value as string
            )
            break
          case 'commaSeparatedList':
            ;(this as AnonymousData)[config.property] = (value as string)
              .split(',')
              .map((part) => part.trim())
              .filter((part) => !!part)
            break
          default:
            ;(this as AnonymousData)[config.property] = config.format
              ? config.format(value)
              : value
        }
      }
    }
    if (bound.finalise) {
      const additions = bound.finalise(this)
      for (const key of Object.keys(additions)) {
        ;(this as AnonymousData)[key] = additions[key]
      }
    }
    return this
  }

  update<Data>(attrs: Data) {
    return this.map(attrs)
  }

  getAlias() {
    return undefined
  }
}
